import { Modal } from "@circle-react-uikit/ModalV2";
import { viewOnlyMasqueradeAs } from "@circle-react/api/masqueradingApi";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import type { MasqueradingEntityType } from "@circle-react/types/Masquerading";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useClickAway } from "react-use";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

import { CategoryBackButton } from "./CategoryBackButton";
import { CategorySelector } from "./CategorySelector";
import { NoResults } from "./NoResults";
import { SearchInput } from "./SearchInput";
import { SearchResult } from "./SearchResult";

export const ViewMasqueradingSearchModal = () => {
  const drawer = useModal();
  const modalBodyRef = useRef<any>(null);
  const resultsContainerRef = useRef<any>(null);
  useClickAway(modalBodyRef, () => {
    drawer.remove();
  });

  // These are parameters we will use to trigger new search request
  const [requestCategory, setRequestCategory] = useState("");
  const [requestQuery, setRequestQuery] = useState("");

  // These will be set after search request is finished and we need to render results
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [hasNoResults, setHasNoResults] = useState(false);

  const debouncedRequestQuery = useDebouncedValue(requestQuery, 500);

  const { data, isInitialLoading: isLoading } = useQuery<any, any, any, any>({
    queryKey: ["masquerading/entities", debouncedRequestQuery, requestCategory],
    queryFn: () =>
      reactQueryGet(
        internalApi.masquerading.entities.index({
          query: debouncedRequestQuery,
          category: requestCategory,
        }),
      ),
    refetchOnWindowFocus: false,
    enabled: !!(debouncedRequestQuery || requestCategory),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!(debouncedRequestQuery || requestCategory)) {
      setHasNoResults(false);
      setResults([]);
    }
  }, [debouncedRequestQuery, requestCategory]);

  useEffect(() => {
    if (data) {
      const results = data.records;
      const category = data.category;
      const query = data.query;

      setCategory(category);
      setQuery(query);

      if (category || query) {
        setHasNoResults(results.length === 0);
        setResults(results);
      } else {
        setHasNoResults(false);
        setResults([]);
      }
    }
  }, [data]);

  const handleClick = (
    entityId: string | number,
    entityType: MasqueradingEntityType,
  ) =>
    viewOnlyMasqueradeAs({
      entityId: entityId,
      entityType: entityType,
    })
      .then(data => window.open(data.masquerade_url, "_self"))
      .catch(error => {
        console.error(error);
      });

  return (
    <Modal isOpen={drawer.visible} onClose={drawer.remove}>
      <Modal.Content
        position="top"
        className="border-primary w-full rounded-[0px] sm:rounded-md sm:border md:!mt-[3.25rem] md:w-[335px]"
        size="xl"
      >
        <div ref={modalBodyRef}>
          <SearchInput
            isLoading={isLoading}
            query={requestQuery}
            setQuery={setRequestQuery}
          />
          {results.length > 0 ? (
            <div
              className="mb-2 overflow-y-auto md:max-h-96"
              ref={resultsContainerRef}
            >
              {category && !query && (
                <CategoryBackButton
                  category={category}
                  setCategory={setRequestCategory}
                />
              )}
              <ul className="list-none">
                {results.map(result => (
                  <SearchResult
                    key={`${result.type}-${result.id}`}
                    id={result.id}
                    type={result.type}
                    name={result.name}
                    avatarUrl={result.avatar_url}
                    onClick={handleClick}
                  />
                ))}
              </ul>
            </div>
          ) : hasNoResults ? (
            <NoResults
              setCategory={setRequestCategory}
              setQuery={setRequestQuery}
            />
          ) : (
            <CategorySelector setCategory={setRequestCategory} />
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};
